import React, { useEffect, useState, useRef } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { UploadSimple, LinkSimple, Check, X as XIcon, ArrowRight } from "@phosphor-icons/react";

export default function ReconciliationPage() {
  const [accts, setAccts] = useState([]);
  const [selected, setSelected] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [allLines, setAllLines] = useState([]);
  const [allTxns, setAllTxns] = useState([]);
  const [activeLine, setActiveLine] = useState(null);
  const [activeTxn, setActiveTxn] = useState(null);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const loadAccts = async () => {
    const { data } = await api.get("/bank-accounts");
    setAccts(data);
    if (!selected && data[0]) setSelected(data[0].id);
  };
  useEffect(() => { loadAccts(); }, []);

  const loadData = async () => {
    if (!selected) return;
    const [s, l, t] = await Promise.all([
      api.get(`/reconciliation/suggestions?bank_account_id=${selected}`),
      api.get(`/bank-statements?bank_account_id=${selected}`),
      api.get(`/transactions?bank_account_id=${selected}`),
    ]);
    setSuggestions(s.data.suggestions || []);
    setAllLines(l.data);
    setAllTxns(t.data);
  };
  useEffect(() => { loadData(); }, [selected]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post(`/bank-statements/upload-csv?bank_account_id=${selected}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setMsg(`Imported ${data.imported} lines from CSV.`);
    e.target.value = "";
    loadData();
  };

  const syncLean = async () => {
    if (!selected) return;
    const { data } = await api.post("/bank-statements/sync-lean", { bank_account_id: selected });
    setMsg(data.message || "Synced");
    loadData();
  };

  const acceptSuggestion = async (bank_line_id, transaction_id) => {
    await api.post("/reconciliation/match", { bank_line_id, transaction_id });
    loadData();
  };

  const manualMatch = async () => {
    if (!activeLine || !activeTxn) return;
    await api.post("/reconciliation/match", { bank_line_id: activeLine, transaction_id: activeTxn });
    setActiveLine(null); setActiveTxn(null);
    loadData();
  };

  const unmatch = async (bank_line_id, transaction_id) => {
    await api.post("/reconciliation/unmatch", { bank_line_id, transaction_id });
    loadData();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Cash Reconciliation"
        title="Reconciliation Workbench"
        subtitle="Match bank statement lines with system transactions — auto-suggested or manual."
        testid="reco-title"
        actions={
          <>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="reco-bank-select" className="border border-gray-300 px-3 py-2 text-sm font-mono">
              <option value="">Select account…</option>
              {accts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
            </select>
            <input type="file" accept=".csv" ref={fileRef} onChange={upload} className="hidden" data-testid="reco-file-input" />
            <button onClick={() => fileRef.current?.click()} data-testid="upload-csv-btn" disabled={!selected} className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2">
              <UploadSimple size={14} weight="bold" /> Upload CSV
            </button>
            <button onClick={syncLean} disabled={!selected} data-testid="sync-lean-btn" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2">
              <LinkSimple size={14} weight="bold" /> Sync via Lean
            </button>
          </>
        }
      />

      {msg && <div className="mx-8 mt-6 border border-gray-300 bg-gray-50 px-4 py-3 text-xs font-mono" data-testid="reco-msg">{msg}</div>}

      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bank statement lines */}
        <div className="border border-gray-200 bg-white">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Source</div>
              <h3 className="text-xl font-black tracking-tight mt-0.5">Bank Statement Lines</h3>
            </div>
            <div className="text-xs font-mono text-gray-500">{allLines.length} lines</div>
          </div>
          <table className="w-full text-sm" data-testid="bank-lines-table">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Date","Reference","Description","Amount","Status",""].map((h) => <th key={h} className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${h === "Amount" ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {allLines.map((l) => (
                <tr key={l.id} className={`border-b border-gray-100 cursor-pointer ${activeLine === l.id ? "bg-blue-50" : "hover:bg-gray-50"} ${l.matched ? "opacity-60" : ""}`} onClick={() => !l.matched && setActiveLine(l.id)} data-testid={`bank-line-${l.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{fmtDate(l.value_date)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{l.reference || "—"}</td>
                  <td className="px-3 py-2 text-xs truncate max-w-[140px]">{l.description}</td>
                  <td className={`px-3 py-2 text-right font-mono ${l.amount < 0 ? "text-[#DC2626]" : ""}`}>{fmt(l.amount)}</td>
                  <td className="px-3 py-2 text-[10px] uppercase font-bold">{l.matched ? "Matched" : "Open"}</td>
                  <td className="px-3 py-2">
                    {l.matched && <button onClick={(e) => { e.stopPropagation(); unmatch(l.id, l.matched_transaction_id); }} data-testid={`unmatch-${l.id}`}><XIcon size={12} className="text-[#DC2626]" /></button>}
                  </td>
                </tr>
              ))}
              {allLines.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-xs text-gray-500">No statement lines. Upload CSV to begin.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* System Transactions */}
        <div className="border border-gray-200 bg-white">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">System</div>
              <h3 className="text-xl font-black tracking-tight mt-0.5">Ledger Transactions</h3>
            </div>
            <button onClick={manualMatch} disabled={!activeLine || !activeTxn} data-testid="manual-match-btn" className="bg-[#0020C2] text-white px-3 py-2 text-[10px] font-bold uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-30 inline-flex items-center gap-1">
              Match <ArrowRight size={12} weight="bold" />
            </button>
          </div>
          <table className="w-full text-sm" data-testid="ledger-txns-table">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Date","Type","Reference","Amount","Status"].map((h) => <th key={h} className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${h === "Amount" ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {allTxns.map((t) => (
                <tr key={t.id} className={`border-b border-gray-100 cursor-pointer ${activeTxn === t.id ? "bg-blue-50" : "hover:bg-gray-50"} ${t.reconciled ? "opacity-60" : ""}`} onClick={() => !t.reconciled && setActiveTxn(t.id)} data-testid={`ledger-txn-${t.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{fmtDate(t.value_date)}</td>
                  <td className="px-3 py-2 text-[10px] uppercase font-bold tracking-wider">{t.txn_type.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 font-mono text-xs">{t.reference || "—"}</td>
                  <td className={`px-3 py-2 text-right font-mono ${(t.direction_sign ?? 0) < 0 ? "text-[#DC2626]" : ""}`}>{fmt(t.direction_sign ?? t.amount)}</td>
                  <td className="px-3 py-2 text-[10px] uppercase font-bold">{t.reconciled ? "Reconciled" : "Open"}</td>
                </tr>
              ))}
              {allTxns.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-gray-500">No transactions for this account.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Suggestions */}
        <div className="border border-gray-200 bg-white lg:col-span-2">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Engine</div>
            <h3 className="text-xl font-black tracking-tight mt-0.5">Auto-Match Suggestions</h3>
          </div>
          <div className="divide-y divide-gray-200" data-testid="suggestions-list">
            {suggestions.length === 0 && <div className="px-6 py-8 text-sm text-gray-500 text-center">No open bank lines to suggest. Upload a CSV or check above.</div>}
            {suggestions.map((s) => (
              <div key={s.bank_line.id} className="px-6 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4 items-center bg-gray-50/50">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Bank Line</div>
                  <div className="text-sm font-semibold mt-1">{s.bank_line.description}</div>
                  <div className="font-mono text-xs text-gray-500 mt-0.5">{fmtDate(s.bank_line.value_date)} · {fmt(s.bank_line.amount)} {s.bank_line.currency}</div>
                </div>
                <div className="lg:col-span-2">
                  {s.candidates.length === 0 ? (
                    <div className="text-xs text-gray-500 italic">No matching system transaction found.</div>
                  ) : (
                    <div className="space-y-2">
                      {s.candidates.map((c) => (
                        <div key={c.transaction.id} className="border border-gray-200 bg-white px-3 py-2 flex items-center justify-between text-sm">
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{c.transaction.txn_type.replace(/_/g, " ")}</div>
                            <div className="font-mono text-xs">{fmtDate(c.transaction.value_date)} · {fmt(c.transaction.direction_sign ?? c.transaction.amount)} {c.transaction.currency}</div>
                            <div className="text-xs text-gray-600">{c.transaction.description}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-[10px] tracking-wider uppercase font-bold text-gray-500">{c.score} score</div>
                            <button onClick={() => acceptSuggestion(s.bank_line.id, c.transaction.id)} data-testid={`accept-match-${s.bank_line.id}-${c.transaction.id}`} className="bg-[#0020C2] text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-[#001AA3] inline-flex items-center gap-1">
                              <Check size={12} weight="bold" /> Accept
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
